import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Outlet, useLocation } from "react-router";
import instance from "../apiInstance";
import { resetState } from "../redux/authSlice";
import { mustRegisterAgain } from "./isGateLeadOnly";
import { isColetaComplete } from "../coletaProfile";
import { trackClient } from "../trackerBus";

const spinner = (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#1a1a1a",
    }}
  >
    <div
      style={{
        width: 40,
        height: 40,
        border: "3px solid rgba(212,175,55,0.2)",
        borderTopColor: "#D4AF37",
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
      }}
    />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

/** Home/news: navega primeiro; cadastro abre depois (CompleteProfileModal). */
const SOFT_COLETA_PATHS = new Set(["/home", "/news"]);

const coletaBlockScreen = (
  <div
    style={{
      minHeight: "100vh",
      background: "#1a1a1a",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 24,
      textAlign: "center",
    }}
  >
    <div>
      <p style={{ color: "#D4AF37", fontSize: 18, marginBottom: 8, fontWeight: 600 }}>
        Complete seu cadastro para continuar
      </p>
      <p style={{ color: "#888", fontSize: 14, maxWidth: 320, margin: "0 auto" }}>
        Preencha o formulário na tela para acessar promoções, leilões e o restante do app.
      </p>
    </div>
  </div>
);

const AppRegisteredGate = () => {
  const { token } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const location = useLocation();
  const [status, setStatus] = useState(token ? "loading" : "anon");
  const [leadEmail, setLeadEmail] = useState("");
  const [gateUser, setGateUser] = useState(null);
  const gateUserRef = React.useRef(null);

  const verifyUser = React.useCallback((retries = 0) => {
    return instance.get("/me").then((res) => {
      const user = res.data?.user;
      if (mustRegisterAgain(user)) {
        setLeadEmail((user?.email || "").trim().toLowerCase());
        dispatch(resetState());
        setStatus("lead");
        return;
      }
      gateUserRef.current = user;
      setGateUser(user);
      if (!isColetaComplete(user)) {
        setStatus("coleta");
        return;
      }
      setStatus("ok");
    }).catch((err) => {
      if (err?.response?.status === 401) {
        setStatus("anon");
        return;
      }
      if (retries < 2) {
        return new Promise((resolve) => {
          setTimeout(resolve, 800);
        }).then(() => verifyUser(retries + 1));
      }
      const cached = gateUserRef.current;
      if (cached && !isColetaComplete(cached)) {
        setStatus("coleta");
        return;
      }
      setStatus("loading");
    });
  }, [dispatch]);

  useEffect(() => {
    if (!token) {
      setStatus("anon");
      return undefined;
    }

    let cancelled = false;
    verifyUser().catch(() => {
      if (cancelled) return;
    });

    return () => {
      cancelled = true;
    };
  }, [token, verifyUser, location.pathname]);

  useEffect(() => {
    if (status !== "coleta") return undefined;

    const soft = SOFT_COLETA_PATHS.has(location.pathname);
    if (!soft) {
      trackClient("coleta_access_blocked", {
        page: location.pathname,
        metadata: {
          reason: "route_blocked",
          attempted_path: location.pathname,
          name: gateUser?.name || null,
        },
      });
    } else {
      trackClient("coleta_soft_home", {
        page: location.pathname,
        metadata: {
          reason: "home_allowed_pending_coleta",
          name: gateUser?.name || null,
        },
      });
    }
    window.dispatchEvent(
      new CustomEvent("coleta_gate_required", {
        detail: { user: gateUser, soft, path: location.pathname },
      })
    );
    return undefined;
  }, [location.pathname, status, gateUser]);

  useEffect(() => {
    const onComplete = () => {
      verifyUser(location.pathname).catch(() => {});
    };
    window.addEventListener("cadastro_completed", onComplete);
    return () => window.removeEventListener("cadastro_completed", onComplete);
  }, [verifyUser, location.pathname]);

  if (status === "anon") return <Navigate to="/" replace />;
  if (status === "loading") return spinner;
  if (status === "lead") {
    const q = leadEmail ? `?email=${encodeURIComponent(leadEmail)}` : "";
    return <Navigate to={`/register${q}`} replace />;
  }
  if (status === "coleta") {
    if (SOFT_COLETA_PATHS.has(location.pathname)) {
      return <Outlet />;
    }
    return coletaBlockScreen;
  }
  return <Outlet />;
};

export default AppRegisteredGate;
